import { Spinner } from '@common/components';
import { analytics } from '@features/analytics';
import {
  getSpaSettingsPrefetch,
  getSpaTreatmentPrefetch,
  useGetSpaTreatment,
} from '@features/spa/hooks';
import { PagePropsWithSSR } from '@features/state';
import SpaTreatment from '@features/spa/pages/SpaTreatment';
import { yourJourneySelectors } from '@store/yourJourney';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { parseDateToString } from '@utils/helpers';
import moment from 'moment';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Error from 'next/error';
import { Routes } from '@common/constants';
import { entityQueryOutcome } from '@common/helpers/entityQueryOutcome';
import { useRedirectWhenMissing } from '@common/hooks';
import { redirectToListing } from '@common/helpers/notFoundRedirect';
import Head from 'next/head';
import { NextRouter, withRouter } from 'next/router';
import { useEffect } from 'react';
import { Property } from 'shared-types/Property';
import { SpaEntityType } from 'shared-types/Spa';

type SpaTreatmentPageProps = PagePropsWithSSR<{
  id: string;
  property: Property;
  language: string;
  spaTreatment: SpaEntityType;
  url: string;
  socialFallbackImage: { url?: string };
  router: NextRouter;
  spaDate: string;
}>;

const SpaTreatmentPage: NextPage<SpaTreatmentPageProps> = ({
  id,
  property,
  language,
  socialFallbackImage = { url: '' },
  url,
  router,
  spaDate,
}) => {
  const {
    data: spaTreatment,
    isFetching,
    error,
  } = useGetSpaTreatment(property.id, language, id, spaDate);
  const { isMissing, hasFailed } = entityQueryOutcome({ data: spaTreatment, isFetching, error });

  useRedirectWhenMissing(isMissing, Routes.SPA_TREATMENT_LIST);

  const pageTitle = property.propertyTitle
    ? `${spaTreatment?.name} | ${property.propertyTitle}`
    : spaTreatment?.name;
  const { bookingStep } = router.query;

  useEffect(() => {
    spaTreatment &&
      analytics.track({
        event: 'view_item',
        params: {
          currency: spaTreatment.price.currencyCode,
          value: spaTreatment.price.amount,
          items: [
            {
              item_category: 'spa-treatments',
              item_id: `${spaTreatment.id}`,
              item_name: spaTreatment.name,
              check_in: moment(spaDate).format('YYYY-MM-DD'),
            },
          ],
        },
      });
  }, [spaTreatment]);

  // The API failed, so we do not know whether this treatment exists. Sending the
  // guest to the listing would tell them it is gone.
  if (hasFailed) {
    return <Error statusCode={500} />;
  }

  if (isFetching && (!spaTreatment || Number(spaTreatment.id) !== Number(id))) {
    return <Spinner isActive={isFetching} />;
  }
  if (!spaTreatment) {
    return null;
  }

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            description: spaTreatment?.summaryDescription,
            locale: language,
            url,
            images: [
              {
                url:
                  spaTreatment?.imageGallery && spaTreatment?.imageGallery.length
                    ? spaTreatment?.imageGallery[0].url
                    : socialFallbackImage && socialFallbackImage.url,
              },
            ],
          },
        }}
      />
      <SpaTreatment
        id={id}
        key={id}
        property={property}
        language={language}
        bookingStep={bookingStep}
      />
    </>
  );
};

type SpaTreatmentInitialProps = {
  property: Property;
  language: string;
  url: string;
  store: any;
};

SpaTreatmentPage.getInitialProps = async ({
  property,
  language,
  query,
  url,
  store,
  res,
}: NextPageContext & SpaTreatmentInitialProps): Promise<SpaTreatmentPageProps> => {
  const client = new QueryClient();
  const { spaDate } = yourJourneySelectors.getParsedJourneyParams(store.getState());

  const isServer = !!res;
  const spaTreatment = isServer
    ? (
        await Promise.all([
          getSpaSettingsPrefetch(client, property.id),
          getSpaTreatmentPrefetch(
            client,
            property.id,
            language,
            query.id,
            parseDateToString(spaDate)
          ),
        ])
      )[1]
    : undefined;

  // Only the server looked. On a client navigation the page asks for itself and
  // useRedirectWhenMissing decides once the query settles.
  if (isServer && !spaTreatment) {
    redirectToListing(res, Routes.SPA_TREATMENT_LIST, query);
  }

  return {
    id: query.id as string,
    property,
    language,
    url,
    spaDate: parseDateToString(spaDate),
    socialFallbackImage: property.socialFallbackImage,
    dehydratedState: dehydrate(client),
  } as any;
};

export default withRouter(SpaTreatmentPage as any);
